import React, { PropsWithChildren } from "react";
import { REBOTT_URL_LINK_PORTAL } from "@/config";
import { Typography, Collapse, Navbar as MTNavbar, } from "@material-tailwind/react";

import Image from "next/image";
import Link from "next/link";
import { Bars3Icon, XMarkIcon } from "@heroicons/react/24/outline";

interface NavItemProps {
  children: React.ReactNode;
  href: string;
  onNavigate?: (hash: string) => void;
}

function NavItem({ children, href, onNavigate }: NavItemProps) {
  const isHash = href.startsWith("#");
  const handleClick = (e: React.MouseEvent) => {
    if (isHash) {
      e.preventDefault();
      const hash = href.slice(1);
      const el = document.getElementById(hash);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      onNavigate?.(hash);
    }
  };

  const baseClasses = 
  "px-2 py-2 rounded-lg cursor-pointer hover:text-blue-700 hover:bg-[#eff6ff]";

  return (
    <li>
      {isHash ? (
        <a href={href} className={baseClasses} onClick={handleClick}>
          <Typography {...({} as any)} as="span" variant="h6" className="font-medium">
            {children}
          </Typography>
        </a>
      ) : (
        <Link
          href={href}
          onClick={() => {
            // ปิดเมนูหรือ scroll handler จะถูกเรียกจาก onNavigate
            onNavigate?.(href);
          }}
        >
          <Typography {...({} as any)} as="span" variant="h6" className={`${baseClasses} font-medium`}>
            {children}
          </Typography>
        </Link>
      )}
    </li>
  );
}

interface NavbarProps extends PropsWithChildren<{
  onNavigate?: (hash: string) => void;
}> {}

const TailwindNavbar =
  MTNavbar as unknown  as React.FC<{
    children: React.ReactNode;
    shadow?: boolean;
    blurred?: boolean;
    color?: string;
    className?: string;
    fullWidth?: boolean;         
  }>;

export function Navbar({ onNavigate, children }: NavbarProps) {
  const [open, setOpen] = React.useState(false);
  const [isScrolling, setIsScrolling] = React.useState(false);

  React.useEffect(() => {
    const onResize = () => window.innerWidth >= 960 && setOpen(false);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // ปิดบน resize desktop
  React.useEffect(() => {
    const onResize = () => window.innerWidth >= 960 && setOpen(false);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // เฝ้าดู scroll เพื่อเปลี่ยน style navbar
  React.useEffect(() => {
    const onScroll = () => setIsScrolling(window.scrollY > 0);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

    // handler รวมทั้ง scroll/navigation และปิดเมนู
    const handleMobileNav = (hrefOrHash: string) => {
      onNavigate?.(hrefOrHash);
      setOpen(false);
    };

  return (
    <TailwindNavbar 
      fullWidth
      shadow={false}
      blurred={false}
      color="white"
      className={`fixed top-0 z-50 !h-[80px] bg-white ${isScrolling ? "" : ""}`}
    >
      <div className="container mx-auto h-full flex items-center justify-between">
        <Link href="/" scroll={false}>
          {/* <Image src="/image/ReBottLogo.webp" width={200} height={40} alt="Rebott Logo" className="cursor-pointer w-auto h-auto"/> */}
          <div className="relative w-64 h-20 md:w-64 md:h-20">
            <Image
              src="/image/ReBottLogo.webp"
              alt="Rebott Logo"
              fill
              className="object-contain"
              priority
            />
          </div>
       
        </Link>
        {/* Desktop menu */}
        <ul className="hidden lg:flex items-center gap-5 text-[#2998E2]">
          <NavItem href="/#product-intro" onNavigate={onNavigate}>หน้าหลัก</NavItem>
          <NavItem href="/#rebott-benefits" onNavigate={onNavigate}>ผลิตภัณฑ์</NavItem>
          <NavItem href="/rebott-locations-page">ตู้ Rebott ของเราตั้งอยู่ที่ไหนบ้าง?</NavItem>
          {/* <NavItem href="/#partners" onNavigate={onNavigate}>พาร์ทเนอร์</NavItem> */}
          <NavItem href="/#contact-us" onNavigate={onNavigate}>ติดต่อเรา</NavItem>
          <NavItem href={REBOTT_URL_LINK_PORTAL} onNavigate={onNavigate}>ลิงก์ที่เกี่ยวข้อง</NavItem>

        </ul>

        {/* Mobile toggle */}
        <button 
            color="blue-gray"  
            slot="button"
            style={{}}
            title="Toggle mobile menu"
            aria-label="เปิดเมนูมือถือ"
            onClick={() => setOpen((o) => !o)}
            className="ml-auto lg:hidden text-[#2998E2]"
        >
            {open ? <XMarkIcon className="w-6 h-6" /> : <Bars3Icon className="w-6 h-6" />}
        </button>
      </div>
      <Collapse open={open}>
        <div className="container mx-auto mt-4 rounded-lg border-t border-blue-gray-50 bg-white px-4 py-2">
          <ul className="flex flex-col gap-0 p-2 text-[#2998E2]">
            <NavItem href="/#product-intro" onNavigate={handleMobileNav}>หน้าหลัก</NavItem>
            <NavItem href="/#rebott-benefits" onNavigate={handleMobileNav}>ผลิตภัณฑ์</NavItem>
            <NavItem href="/rebott-locations-page">ตู้ Rebott ของเราตั้งอยู่ที่ไหนบ้าง?</NavItem>
            <NavItem href="/#partners" onNavigate={handleMobileNav}>พาร์ทเนอร์</NavItem>
            <NavItem href="/#contact-us" onNavigate={handleMobileNav}>ติดต่อเรา</NavItem>
            <NavItem href={REBOTT_URL_LINK_PORTAL} onNavigate={handleMobileNav}>ลิงก์ที่เกี่ยวข้อง</NavItem>
          </ul>
        </div>
      </Collapse>
    </TailwindNavbar>
  );
}

export default Navbar;
